import { auth } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  updateProfile,
  verifyBeforeUpdateEmail
} from 'firebase/auth';

// Set persistence to LOCAL - keeps users logged in until explicit logout
// This is important for PWA to work smoothly
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('✅ Auth persistence set to LOCAL - users will stay logged in');
  })
  .catch((error) => {
    console.error('❌ Error setting auth persistence:', error);
  });

export const authService = {
  // Sign up new user
  async signUp(email, password, displayName = null) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Signup error:', error.code);
      
      // Provide user-friendly error messages
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'This email is already registered. Please login instead or use a different email.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters long.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled. Please contact support.';
          break;
        default:
          errorMessage = 'Unable to create account. Please try again.';
          break;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // Send email verification
  async sendVerificationEmail(user = null) {
    try {
      const targetUser = user || auth.currentUser;
      if (!targetUser) {
        return { success: false, error: 'No user logged in' };
      }
      
      // Action code settings - customize the verification email
      const actionCodeSettings = {
        url: `${window.location.origin}/auth-action?verified=true`,
        handleCodeInApp: false
      };
      
      await sendEmailVerification(targetUser, actionCodeSettings);
      console.log('✅ Verification email sent to:', targetUser.email);
      return { success: true };
    } catch (error) {
      console.error('Error sending verification email:', error.code);
      
      let errorMessage = error.message;
      switch (error.code) {
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please wait a few minutes before trying again.';
          break;
        default:
          errorMessage = 'Unable to send verification email. Please try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // Sign in existing user
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Login error:', error.code);
      
      // Provide user-friendly error messages
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email. Please sign up first.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed login attempts. Please try again later or reset your password.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password. Please check and try again.';
          break;
        default:
          errorMessage = 'Unable to sign in. Please try again.';
          break;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // Sign out current user
  async signOut() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Signout error:', error.code);
      return { success: false, error: 'Unable to sign out. Please try again.' };
    }
  },

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Check if email is verified
  isEmailVerified() {
    return auth.currentUser?.emailVerified || false;
  },

  // Reload user to get latest email verification status
  async reloadUser() {
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        return { success: true, user: auth.currentUser };
      }
      return { success: false, error: 'No user logged in' };
    } catch (error) {
      console.error('Error reloading user:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user email (with verification)
  async updateUserEmail(newEmail) {
    try {
      if (!auth.currentUser) return { success: false, error: 'No user logged in' };
      
      const actionCodeSettings = {
        url: `${window.location.origin}/auth-action?verified=true`,
        handleCodeInApp: false
      };
      
      // verification required in modern Firebase auth
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail, actionCodeSettings);
      return { success: true, requiresVerification: true };
    } catch (error) {
       console.error('Update email error:', error);
       let errorMessage = error.message;
       if (error.code === 'auth/requires-recent-login') {
         errorMessage = 'Security Verification: Please log out and log back in before attempting to change your email address.';
       } else if (error.code === 'auth/email-already-in-use') {
         errorMessage = 'This email is already registered to another account.';
       } else if (error.code === 'auth/invalid-email') {
         errorMessage = 'Please provide a valid email format.';
       } else if (error.code === 'auth/operation-not-allowed') {
         errorMessage = 'Email updates are currently disabled or require additional configuration in Firebase console.';
       }
       return { success: false, error: errorMessage };
    }
  }
};

export default authService;